import type { MapDocument } from './types';

const DB_NAME = 'ifw-drafts';
const STORE_NAME = 'maps';

export class DraftStore {
  private readonly database: Promise<IDBDatabase | null>;

  constructor() {
    this.database = this.open();
  }

  async save(document: MapDocument): Promise<void> {
    const database = await this.database;
    if (!database) return;
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite');
      transaction.objectStore(STORE_NAME).put({ id: document.id, savedAt: Date.now(), document });
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async load(id: string): Promise<{ savedAt: number; document: MapDocument } | null> {
    const database = await this.database;
    if (!database) return null;
    return new Promise((resolve) => {
      const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
    });
  }

  async remove(id: string): Promise<void> {
    const database = await this.database;
    if (!database) return;
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(id);
  }

  private open(): Promise<IDBDatabase | null> {
    if (!('indexedDB' in window)) return Promise.resolve(null);
    return new Promise((resolve) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
  }
}

